import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";

import {
  agentRunsApi, type AgentRunDetailDto, type AgentRunDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";


export function AgentRunsPage(): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);
  if (openId) {
    return <RunDetail runId={openId} onBack={() => setOpenId(null)} />;
  }
  return <RunsList onOpen={setOpenId} />;
}


function RunsList({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const t = useT();
  const { data, error, loading, refetch } = useFetch<AgentRunDto[]>(
    (signal) => agentRunsApi.list({ limit: 100 }, { signal }),
    [],
  );

  return (
    <Card>
      <CardHeader
        title={t("agent_runs.title")}
        description={t("agent_runs.subtitle")}
        actions={<Button variant="ghost" size="xs" onClick={refetch}>{t("common.refresh")}</Button>}
      />

      {loading && !data && <SkeletonTable rows={8} cols={5} />}
      {error && <ErrorBlock error={error} onRetry={refetch} />}
      {data && data.length === 0 && (
        <EmptyBlock title={t("agent_runs.empty")} />
      )}

      {data && data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-base-200 text-left text-2xs uppercase tracking-wide text-base-500">
                <th className="py-2 pr-3 font-medium">{t("agent_runs.col.time")}</th>
                <th className="py-2 pr-3 font-medium">{t("agent_runs.col.agent")}</th>
                <th className="py-2 pr-3 font-medium">{t("agent_runs.col.trigger")}</th>
                <th className="py-2 pr-3 font-medium">{t("agent_runs.col.status")}</th>
                <th className="py-2 pr-3 font-medium text-right">{t("agent_runs.col.duration")}</th>
                <th className="py-2 pr-3 font-medium text-right">LLM</th>
                <th className="py-2 pr-3 font-medium text-right">$</th>
                <th className="py-2 pr-3 font-medium">correlation_id</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-b border-base-100 hover:bg-base-50"
                  onClick={() => onOpen(r.id)}
                >
                  <td className="py-2 pr-3 font-mono text-2xs text-base-500">
                    {new Date(r.started_at).toLocaleString("ru-RU")}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-base-700">{r.agent_id}</td>
                  <td className="py-2 pr-3 text-xs text-base-700">{r.trigger_type}</td>
                  <td className="py-2 pr-3">
                    <Badge
                      tone={
                        r.status === "completed" ? "success" :
                        r.status === "failed"    ? "danger"  :
                        r.status === "running"   ? "ai"      : "neutral"
                      }
                      dot
                    >
                      {r.status}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-base-700">
                    {r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(2)}s` : "—"}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-base-700">
                    {r.total_llm_calls}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-base-700">
                    ${r.total_cost_usd.toFixed(4)}
                  </td>
                  <td className="py-2 pr-3 font-mono text-2xs text-base-500">
                    {r.correlation_id ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


function RunDetail({ runId, onBack }: { runId: string; onBack: () => void }): JSX.Element {
  const t = useT();
  const { data, error, loading, refetch } = useFetch<AgentRunDetailDto>(
    (signal) => agentRunsApi.get(runId, { signal }),
    [runId],
  );

  return (
    <div className="flex flex-col gap-3">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft className="size-4" />}
        onClick={onBack}
      >
        {t("agent_runs.back")}
      </Button>

      {error && <ErrorBlock error={error} onRetry={refetch} />}
      {loading && !data && <LoadingBlock />}
      {data && (
        <Card>
          <CardHeader
            title={data.agent_id}
            description={`run ${data.id}`}
            actions={
              <Badge
                tone={
                  data.status === "completed" ? "success" :
                  data.status === "failed"    ? "danger"  : "ai"
                }
                dot
              >
                {data.status}
              </Badge>
            }
          />

          <dl className="mb-4 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-sm">
            <KV label={t("agent_runs.detail.triggered_by")} value={<code className="font-mono text-xs">{data.trigger_type}</code>} />
            <KV label="correlation_id" value={<code className="font-mono text-xs">{data.correlation_id ?? "—"}</code>} />
            <KV label={t("agent_runs.detail.started")}  value={new Date(data.started_at).toLocaleString("ru-RU")} />
            <KV label={t("agent_runs.detail.finished")} value={data.finished_at ? new Date(data.finished_at).toLocaleString("ru-RU") : "—"} />
            <KV label={t("agent_runs.detail.duration")} value={data.duration_ms != null ? `${(data.duration_ms / 1000).toFixed(2)}s` : "—"} />
            <KV label="LLM calls"      value={String(data.total_llm_calls)} />
            <KV label="Cost"           value={`$${data.total_cost_usd.toFixed(4)}`} />
          </dl>

          {data.error_message && (
            <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-800">
              <div className="font-medium">{t("agent_runs.detail.error")}</div>
              <div className="mt-1 break-words">{data.error_message}</div>
            </div>
          )}

          <SnapshotBlock title={t("agent_runs.input")}  data={data.input_snapshot} />
          <SnapshotBlock title={t("agent_runs.output")} data={data.output_snapshot} />

          {data.llm_calls.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-2xs font-medium uppercase tracking-wide text-base-500">
                LLM calls ({data.llm_calls.length})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-base-200 text-left text-2xs uppercase tracking-wide text-base-500">
                      <th className="py-1.5 pr-3 font-medium">Time</th>
                      <th className="py-1.5 pr-3 font-medium">Task</th>
                      <th className="py-1.5 pr-3 font-medium">Provider/Model</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Tokens (in/out)</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Latency</th>
                      <th className="py-1.5 pr-3 font-medium text-right">$</th>
                      <th className="py-1.5 pr-3 font-medium">cache</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono tabular-nums">
                    {data.llm_calls.map((c) => (
                      <tr key={c.id} className="border-b border-base-100">
                        <td className="py-1.5 pr-3 text-base-500">
                          {new Date(c.created_at).toLocaleTimeString("ru-RU")}
                        </td>
                        <td className="py-1.5 pr-3">{c.task_type} · {c.complexity}</td>
                        <td className="py-1.5 pr-3">{c.provider}/{c.model}</td>
                        <td className="py-1.5 pr-3 text-right">
                          {c.input_tokens} / {c.output_tokens}
                        </td>
                        <td className="py-1.5 pr-3 text-right">{c.latency_ms}ms</td>
                        <td className="py-1.5 pr-3 text-right">${c.cost_usd.toFixed(4)}</td>
                        <td className="py-1.5 pr-3 text-2xs">
                          {c.cache_hit ? "hit" : "miss"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}


function KV({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <>
      <dt className="text-base-500">{label}</dt>
      <dd className="text-base-900">{value}</dd>
    </>
  );
}


function SnapshotBlock({
  title, data,
}: { title: string; data: Record<string, unknown> }): JSX.Element {
  return (
    <details className="mb-2 rounded-md border border-base-200 bg-base-50 p-2 open:bg-surface">
      <summary className="cursor-pointer text-2xs font-medium uppercase tracking-wide text-base-700">
        {title}
      </summary>
      <pre className="mt-2 overflow-x-auto rounded-sm bg-base-900 p-2 text-2xs leading-relaxed text-base-100">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}
